import { useState } from "react";
import { useHistory } from "react-router";

import { FlightError } from "@b2bportal/air-booking-api";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  type PriceFreezeError,
  PriceFreezeErrorEnum,
} from "@b2bportal/price-freeze-api";
import {
  type PaymentError,
  Product,
  type ProductError,
  type PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import {
  AirChfarExerciseDiscountSelectors,
  AirPFExerciseSelectors,
  CartQuoteErrorModal,
  CartQuoteEventType,
  cartQuoteSelectors,
  CartQuoteState,
  DesktopLayout,
  getChildState,
  useCheckoutSend,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CHFAR_ID_QUERY_PARAM } from "@hopper-b2b/types";
import {
  B2BLoadingPopup,
  B2BSpinner,
  ErrorModal,
  LoadingScreenWithProgress,
  WorkingBunnies,
} from "@hopper-b2b/ui";
import { getEnvVariables, useDeviceTypes } from "@hopper-b2b/utilities";

import AirplaneFintech from "../../../../assets/client/airplane-fintech.svg";
import Clock from "../../../../assets/client/clock-solo.svg";
import DollarBill from "../../../../assets/client/dollar-bill.svg";
import Error from "../../../../assets/client/error.svg";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import type { TEvent } from "../../events";
import { getClientAssets } from "../../selectors";
import type { FlightMachineContext } from "../../types";
import { ConnectedPriceBreakdownContent } from "../PriceBreakdownDropdown";
import { PriceFreezeErrorModal } from "../PriceFreezeErrorModal";
import "./styles.scss";

export const CartQuoteComponent = (props) => {
  const { t } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();

  const clientAssets = useSelector(getClientAssets);
  const [state] = useCheckoutState<TEvent, FlightMachineContext>();
  const childState = getChildState(state.value);
  const openCartQuoteModal = useSelector(
    cartQuoteSelectors.getOpenCartQuoteModal
  );

  const [resetLoader, setResetLoader] = useState<boolean>(false);

  const getMessage = (): string => {
    switch (childState) {
      // Schedule happens so fast, the text blinks in and gets replaced by polling
      // case CartQuoteState.schedule:
      //   return t("passengerInformation.validatingPassengers");
      case CartQuoteState.schedule:
      case CartQuoteState.polling:
        return t("savingTraveler");
      default:
        return "";
    }
  };

  const loadingSteps = [
    {
      title: t("pollQuoteLoading.step1Title"),
      image: DollarBill,
    },
    {
      title: t("pollQuoteLoading.step2Title"),
      image: AirplaneFintech,
    },
    {
      title: t("pollQuoteLoading.step3Title"),
      image: Clock,
    },
  ];

  const callFailed = useSelector(cartQuoteSelectors.getOpenCartQuoteErrorModal);
  const callSuccessful = useSelector(cartQuoteSelectors.getCartQuoteError)
    ?.data?.[0];

  const loading = !(callFailed || callSuccessful);

  return (
    <>
      {matchesMobile ? (
        <LoadingScreenWithProgress
          className="cart-quote-loading-popup"
          loadingSteps={loadingSteps}
          averageLoadingTime={getEnvVariables("pollQuoteLoadingTime")}
          loading={loading}
          resetLoader={resetLoader}
          handleResetLoader={setResetLoader}
        />
      ) : (
        <>
          <DesktopLayout
            clientLogo={clientAssets?.logo}
            rightContent={<ConnectedPriceBreakdownContent />}
          >
            <B2BSpinner />
          </DesktopLayout>
          <B2BLoadingPopup
            open={openCartQuoteModal}
            message={getMessage()}
            popupSize={"desktop"}
            loadingIcon={<WorkingBunnies />}
            className="schedule-quote-loading"
          />
        </>
      )}
      <CartErrorModal setResetLoader={setResetLoader} />
    </>
  );
};

export const CartErrorModal = (props) => {
  const { setResetLoader } = props;
  const cartErrorModal = useSelector(cartQuoteSelectors.getCartQuoteError);
  const open = useSelector(cartQuoteSelectors.getOpenCartQuoteErrorModal);

  const showPriceFreezeErrorModal = (
    type?: CartQuoteErrorModal,
    data?: PurchaseError[]
  ) => {
    switch (type) {
      case CartQuoteErrorModal.FailedPolling: {
        const head = data?.[0]; // todo: we probably want to have some sort of priority order on errors?
        if (!head) return false;

        switch (head.Error) {
          case PurchaseErrorEnum.NoAvailability:
            return true;
          case PurchaseErrorEnum.ProductError: {
            const productError = head as ProductError;
            if (productError.value.type === Product.Flight) {
              const flightsError = productError.value.value as FlightError;
              return flightsError === FlightError.NoAvailability;
            }
            return false;
          }
          default:
            return false;
        }
      }
      default:
        return false;
    }
  };

  const priceFreezeId = useSelector(AirPFExerciseSelectors.getPriceFreezeId);
  const shouldShowPriceFreezeErrorModal =
    priceFreezeId &&
    showPriceFreezeErrorModal(
      cartErrorModal?.type,
      cartErrorModal?.data as PurchaseError[]
    );

  return shouldShowPriceFreezeErrorModal ? (
    <PriceFreezeErrorModal openModal={open} />
  ) : (
    <MappedErrorModal setResetLoader={setResetLoader} />
  );
};

export const MappedErrorModal = (props) => {
  const { t } = useI18nContext();
  const history = useHistory();
  const send = useCheckoutSend<TEvent>();
  const cartErrorModal = useSelector(cartQuoteSelectors.getCartQuoteError);
  const open = useSelector(cartQuoteSelectors.getOpenCartQuoteErrorModal);
  const allowedQuoteRetry = useSelector(cartQuoteSelectors.getAllowQuoteRetry);
  const chfarId = useSelector(AirChfarExerciseDiscountSelectors.getChfarId);
  const { setResetLoader } = props;

  const clearError = () => send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
  const searchAgain = () => {
    clearError();
    //ChFAR exercise
    if (chfarId)
      history.push(`${PATH_FLIGHTS_SEARCH}?${CHFAR_ID_QUERY_PARAM}=${chfarId}`);
    else history.push(PATH_FLIGHTS_SEARCH);
  };
  const updateTravelers = () => {
    send(CartQuoteEventType.OPEN_PASSENGER_PICKER);
    clearError();
  };
  const retryQuote = () => {
    setResetLoader(true);
    send(CartQuoteEventType.RETRY_QUOTE);
  };

  const DEFAULT_ERROR_MODEL_PROPS = {
    title: t("checkoutError.genericErrorTitle"),
    subtitle: t("checkoutError.genericErrorSubtitle"),
    ...(allowedQuoteRetry
      ? {
          primaryButtonText: t("checkoutError.tryAgain"),
          primaryOnClick: retryQuote,
          secondaryButtonText: t("checkoutError.findNewFlight"),
          secondaryOnClick: searchAgain,
        }
      : {
          primaryButtonText: t("checkoutError.findNewFlight"),
          primaryOnClick: searchAgain,
        }),
  };

  const getFlightError = (flightError: FlightError) => {
    switch (flightError) {
      case FlightError.DuplicatedPassport:
        return {
          title: t("checkoutError.duplicatedPassportTitle"),
          subtitle: t("checkoutError.duplicatedPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.CheckInMinimumAgeNotMet:
        return {
          title: t("checkoutError.checkInMinimumAgeNotMetTitle"),
          subtitle: t("checkoutError.checkInMinimumAgeNotMetSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ExpiringPassport:
        return {
          title: t("checkoutError.expiringPassportTitle"),
          subtitle: t("checkoutError.expiringPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ExpiredPassport:
        return {
          title: t("checkoutError.expiredPassportTitle"),
          subtitle: t("checkoutError.expiredPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.IllegalLapInfantKTN:
        return {
          title: t("checkoutError.illegalLapInfantKTNTitle"),
          subtitle: t("checkoutError.illegalLapInfantKTNSubtitle"), // todo: we don't have the pax name
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.IllegalLapInfantRedress:
        return {
          title: t("checkoutError.illegalLapInfantRedressTitle"),
          subtitle: t("checkoutError.illegalLapInfantRedressSubtitle"), // todo: we don't have the pax name
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidCustomer:
        return {
          title: t("checkoutError.invalidCustomerTitle"),
          subtitle: t("checkoutError.invalidCustomerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassengers:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassengerName:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerName"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassportNumber:
        return {
          title: t("checkoutError.invalidPassportNumberTitle"),
          subtitle: t("checkoutError.invalidPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.LapInfantTooOld:
        return {
          title: t("checkoutError.lapInfantTooOldTitle"),
          subtitle: t("checkoutError.lapInfantTooOldSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.LapInfantsUnsupported:
        return {
          title: t("checkoutError.lapInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.lapInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MalformedKnownTravelerNumber:
        return {
          title: t("checkoutError.malformedKnownTravelerNumberTitle"),
          subtitle: t("checkoutError.malformedKnownTravelerNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MalformedRedressNumber:
        return {
          title: t("checkoutError.malformedRedressNumberTitle"),
          subtitle: t("checkoutError.malformedRedressNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MissingAirlineLocator:
        return {
          title: t("checkoutError.missingAirlineLocatorTitle"),
          subtitle: t("checkoutError.missingAirlineLocatorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.MissingNationality:
        return {
          title: t("checkoutError.missingNationalityTitle"),
          subtitle: t("checkoutError.missingNationalitySubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.MissingPassport:
        return {
          title: t("checkoutError.missingPassportTitle"),
          subtitle: t("checkoutError.missingPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MissingPassportNumber:
        return {
          title: t("checkoutError.missingPassportNumberTitle"),
          subtitle: t("checkoutError.missingPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoAvailability:
        return {
          title: t("checkoutError.noAvailabilityTitle"),
          subtitle: t("checkoutError.noAvailabilitySubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.NoAdultPassenger:
        return {
          title: t("checkoutError.noAdultPassengerTitle"),
          subtitle: t("checkoutError.noAdultPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoContactInformation:
        return {
          title: t("checkoutError.noContactInformationTitle"),
          subtitle: t("checkoutError.noContactInformationSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"), // todo: update contact info
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoTicketlessResponse:
        return {
          title: t("checkoutError.noTicketlessResponseTitle"),
          subtitle: t("checkoutError.noTicketlessResponseSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.PassengerNameTooShort:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.passengerNameTooShortSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ProviderError:
        return {
          title: t("checkoutError.providerErrorTitle"),
          subtitle: t("checkoutError.providerErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.SeatedInfantsUnsupported:
        return {
          title: t("checkoutError.seatedInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.seatedInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TooManyLapInfants:
        return {
          title: t("checkoutError.tooManyLapInfantsTitle"),
          subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TooManyPassengers:
        return {
          title: t("checkoutError.tooManyPassengersTitle"),
          subtitle: t("checkoutError.tooManyPassengersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TransientServiceError:
        return {
          title: t("checkoutError.transientServiceErrorTitle"),
          subtitle: t("checkoutError.transientServiceErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.UnknownSabreAppError:
        return {
          title: t("checkoutError.unknownSabreAppErrorTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.UnsupportedNameCharacters:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.unsupportedNameCharactersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.UpgradeRequired:
        return {
          title: t("checkoutError.upgradeRequiredTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      // default FlightErrors that should not occur in CartQuote, or no specific error messaging needed
      case FlightError.DuplicateBooking:
      case FlightError.DuplicatedNationalId:
      case FlightError.ExpiredNationalId:
      case FlightError.ExpiringNationalId:
      case FlightError.ExtraBagsError:
      case FlightError.Inactivity:
      case FlightError.InvalidPassportOrNationalIdNumber:
      case FlightError.MissingNationalId:
      case FlightError.MissingPassportOrNationalId:
      case FlightError.SessionClosed:
      case FlightError.ShopAsyncError:
      case FlightError.TicketingNotStarted:
      case FlightError.TicketingUnconfirmed:
      case FlightError.TicketingUnknown:
      case FlightError.Unknown:
        return DEFAULT_ERROR_MODEL_PROPS;
      default:
        // Will throw a compiler error when there are more FlightError's added to the api
        return ensureExhaustive(flightError, DEFAULT_ERROR_MODEL_PROPS);
    }
  };

  const getAirPriceFreezeError = (pfError: PriceFreezeError) => {
    switch (pfError.PriceFreezeError) {
      case PriceFreezeErrorEnum.NotEnoughSeats:
        return DEFAULT_ERROR_MODEL_PROPS;
      case PriceFreezeErrorEnum.OfferNotFound:
        return DEFAULT_ERROR_MODEL_PROPS;
      case PriceFreezeErrorEnum.PriceFreezeErrorCode:
        return DEFAULT_ERROR_MODEL_PROPS;
      case PriceFreezeErrorEnum.ProductNotFound:
        return DEFAULT_ERROR_MODEL_PROPS;
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getProductError = (error: ProductError) => {
    switch (error.value.type) {
      case Product.AirPriceFreeze: {
        const pfError = error.value.value as PriceFreezeError;
        return getAirPriceFreezeError(pfError);
      }
      case Product.Flight:
      case Product.MultiProviderFlight: {
        const flightError = error.value.value as FlightError;
        return getFlightError(flightError);
      }
      case Product.Seats: {
        // https://github.com/hopper-org/b2b-interfaces/blob/8316205c08da3b4405647ff5245f3bdc76f0ef73/purchase/src/main/scala/com/hopper/b2b/purchase/Models.scala#L114
        const flightError = error.value.value as FlightError;
        return getFlightError(flightError);
      }
      default:
        // covers:
        // - Product.Ground
        // - Product.Hotel
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getPaymentError = (error: PaymentError) => {
    return DEFAULT_ERROR_MODEL_PROPS;
  };

  const getErrorModalProps = (
    type?: CartQuoteErrorModal,
    data?: PurchaseError[]
  ) => {
    switch (type) {
      case CartQuoteErrorModal.FailedPolling: {
        const head = data?.[0]; // todo: we probably want to have some sort of priority order on errors?
        switch (head?.Error) {
          case PurchaseErrorEnum.NoAvailability:
            // todo: No Availability should only be a product error
            return {
              title: t("checkoutError.noAvailabilityTitle"),
              subtitle: t("checkoutError.noAvailabilitySubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PurchaseErrorEnum.PaymentError:
            return getPaymentError(head as PaymentError);
          case PurchaseErrorEnum.ProductError: // todo. Missing `Error` attribute
            return getProductError(head as ProductError);
          default:
            // default case covers
            // - PurchaseErrorEnum.AuthError
            // - PurchaseErrorEnum.ExpiredFulfillToken
            // - PurchaseErrorEnum.ExpiredQuoteToken
            // - PurchaseErrorEnum.FraudAutoReject
            // - PurchaseErrorEnum.InActivity
            // - PurchaseErrorEnum.InvalidSession
            // - PurchaseErrorEnum.NoPaymentsProvided
            // - PurchaseErrorEnum.NonDistinctPayments
            // - PurchaseErrorEnum.ErrorCode (because this isn't standardized enough yet)
            return DEFAULT_ERROR_MODEL_PROPS;
        }
      }
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };
  const errorModalProps = getErrorModalProps(
    cartErrorModal?.type,
    cartErrorModal?.data as PurchaseError[]
  );

  return <ErrorModal open={open} {...errorModalProps} image={Error} />;
};
